import { Users, X } from "lucide-react";
import * as React from "react";
import { ConfirmDialog } from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { EmptyState, ErrorState, TableSkeleton } from "@/components/ui/feedback";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { useToast } from "@/components/ui/toast";
import { WorkflowTrail } from "@/features/workflow-trail";
import { ApiError } from "@/lib/api";
import { cn, formatDateTime, relativeTime } from "@/lib/utils";
import {
  RUN_STATUS_TEXT, actionsDone, endReasonText, endedAt, heldText, nextStepText, pathsTaken,
  type BoardPlace, type WorkflowCatalog, type WorkflowDetail, type WorkflowRun, type WorkflowRunStatus,
} from "@/lib/workflows";
import { RUNS_PAGE, useExitWorkflowRun, useWorkflowRuns } from "@/lib/workflows-api";

const ANY = "__any__";
const TONE: Record<WorkflowRunStatus, "accent" | "success" | "neutral" | "danger"> = {
  active: "accent",
  completed: "success",
  exited: "neutral",
  failed: "danger",
};

/**
 * Enrollment history: the leads a workflow has had, newest first, where each one is now, what it
 * does next and when (from the version that lead is on, which may not be the one published now),
 * and why it finished. An admin takes one lead out from here; it gets nothing more from this
 * workflow. A contact deleted from the console is listed without its name. Read from
 * `GET /workflows/{id}/runs`, a page at a time. A click on a lead opens its trail (ACL-406,
 * `WorkflowTrail`), from where its contact is one more click.
 *
 * A count on the builder's board opens it at the leads that count stands for (`place`, ACL-407):
 * named over the list, and cleared from there.
 */
export function WorkflowLeads({
  workflow, catalog, onShowOnBoard, place = null, placeName = null, onClearPlace,
}: {
  workflow: WorkflowDetail;
  catalog: WorkflowCatalog | undefined;
  onShowOnBoard: (stepId: string) => void;
  place?: BoardPlace | null;
  /** The place in words: `Wait 1 day · waiting now`. */
  placeName?: string | null;
  onClearPlace?: () => void;
}) {
  const workflowId = workflow.id;
  const published = workflow.status === "published";
  const toast = useToast();
  const [trail, setTrail] = React.useState<WorkflowRun | null>(null);
  const [status, setStatus] = React.useState<WorkflowRunStatus | undefined>();
  const [offset, setOffset] = React.useState(0);
  const [removing, setRemoving] = React.useState<WorkflowRun | null>(null);
  const placeKey = place ? `${place.at}/${place.step ?? ""}/${place.path ?? ""}` : "";
  // Another count's leads start on their first page.
  React.useEffect(() => setOffset(0), [placeKey]);
  const runs = useWorkflowRuns(workflowId, { status, place, offset });
  const exit = useExitWorkflowRun();
  const items = runs.data?.items ?? [];
  const total = runs.data?.total ?? items.length;

  // Taking out the last lead of a later page leaves it empty: go back one, rather than show
  // "nobody here" with no way to the leads before.
  React.useEffect(() => {
    if (!runs.isFetching && items.length === 0 && offset > 0) setOffset(Math.max(0, offset - RUNS_PAGE));
  }, [runs.isFetching, items.length, offset]);

  const remove = async (run: WorkflowRun) => {
    try {
      await exit.mutateAsync({ workflowId, runId: run.id });
      toast.success("Taken out of the workflow", run.lead_name ?? "The lead");
      setRemoving(null);
    } catch (e) {
      toast.error("Could not take the lead out", e instanceof ApiError ? e.message : "Please try again.");
    }
  };

  const name = (run: WorkflowRun) => (run.lead_deleted ? "Deleted contact" : (run.lead_name ?? "Unnamed lead"));

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Select
            value={status ?? ANY}
            onValueChange={(v) => {
              setStatus(v === ANY ? undefined : (v as WorkflowRunStatus));
              setOffset(0);
            }}
          >
            <SelectTrigger aria-label="Show leads that are" className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ANY}>Every lead</SelectItem>
              {(Object.keys(RUN_STATUS_TEXT) as WorkflowRunStatus[]).map((s) => (
                <SelectItem key={s} value={s}>
                  {RUN_STATUS_TEXT[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {place && (
            <span className="inline-flex h-8 max-w-full items-center gap-1.5 rounded-full border border-accent-6 bg-accent-2 pl-3 pr-1 text-xs font-medium text-accent-12">
              <span className="truncate" title={placeName ?? undefined}>
                From the board: {placeName ?? "one box"}
              </span>
              {onClearPlace && (
                <button
                  type="button"
                  onClick={onClearPlace}
                  aria-label="Show every lead, not only this box's"
                  title="Show every lead"
                  className="grid size-6 shrink-0 place-items-center rounded-full text-accent-11 hover:bg-accent-4 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40"
                >
                  <X className="size-3.5" />
                </button>
              )}
            </span>
          )}
        </div>
        {total > RUNS_PAGE && (
          <div className="flex items-center gap-2 text-xs text-text-muted">
            {offset + 1}–{Math.min(offset + RUNS_PAGE, total)} of {total}
            <Button variant="outline" size="sm" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - RUNS_PAGE))}>
              Newer
            </Button>
            <Button variant="outline" size="sm" disabled={offset + RUNS_PAGE >= total} onClick={() => setOffset(offset + RUNS_PAGE)}>
              Older
            </Button>
          </div>
        )}
      </div>

      <Card className="overflow-hidden">
        {runs.isPending ? (
          <TableSkeleton cols={4} />
        ) : runs.error ? (
          <ErrorState
            error={runs.error}
            title="Could not load the leads"
            action={
              <Button variant="outline" onClick={() => void runs.refetch()}>
                Try again
              </Button>
            }
          />
        ) : items.length === 0 ? (
          <EmptyState
            icon={Users}
            title={status || place ? "No lead here" : "No lead has entered it yet"}
            description={
              place
                ? "No lead is there now. The board's counts are from when they were last counted: count again on the board to see them as they are."
                : status
                  ? "No lead in this workflow is in that state."
                  : published
                    ? "Each lead it starts for shows here, with the step it is on and what comes next."
                    : "Once it is published, each lead it starts for shows here, with the step it is on."
            }
          />
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Lead</TableHead>
                <TableHead>Where they are</TableHead>
                <TableHead>Entered</TableHead>
                <TableHead>Next</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((run) => {
                const reason = endReasonText(run.end_reason, endedAt(run));
                const held = heldText(run);
                const went = pathsTaken(run).join(" → ");
                const did = actionsDone(run).join(" → ");
                return (
                  <TableRow key={run.id} className="[&>td]:h-12">
                    <TableCell>
                      <button
                        type="button"
                        onClick={() => setTrail(run)}
                        aria-label={`${name(run)}: see the trail`}
                        className={cn(
                          "text-left font-medium underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent-8/40 rounded-sm",
                          run.lead_deleted ? "text-text-muted" : "text-text",
                        )}
                      >
                        {name(run)}
                      </button>
                    </TableCell>
                    <TableCell>
                      <Badge tone={TONE[run.status]} size="sm">
                        {RUN_STATUS_TEXT[run.status]}
                      </Badge>
                      {reason && <p className="text-xs text-text-muted">{reason}</p>}
                      {went && (
                        <p className="max-w-xs truncate text-xs text-text-muted" title={`Went: ${went}`}>
                          Went: {went}
                        </p>
                      )}
                      {did && (
                        <p className="max-w-xs truncate text-xs text-text-muted" title={`Did: ${did}`}>
                          Did: {did}
                        </p>
                      )}
                    </TableCell>
                    <TableCell className="text-xs text-text-muted" title={formatDateTime(run.started_at)}>
                      {relativeTime(run.started_at)}
                    </TableCell>
                    <TableCell className="text-xs text-text">
                      {run.status === "active" ? (
                        <>
                          {nextStepText(run.next_step_type)}
                          {run.next_run_at && (
                            <span className="text-text-muted" title={formatDateTime(run.next_run_at)}>
                              {" "}· {relativeTime(run.next_run_at)}
                            </span>
                          )}
                          {held && <p className="text-text-muted">{held}</p>}
                        </>
                      ) : (
                        <span className="text-text-muted">{run.ended_at ? `Finished ${relativeTime(run.ended_at)}` : "—"}</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {run.status === "active" && (
                        <Button variant="ghost" size="sm" aria-label={`Take ${name(run)} out of the workflow`} onClick={() => setRemoving(run)}>
                          Take out
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <WorkflowTrail
        // The open trail follows the page's copy of the run, so a lead that moves while it is open
        // is shown as it is now.
        run={trail ? (items.find((r) => r.id === trail.id) ?? trail) : null}
        workflow={workflow}
        catalog={catalog}
        onClose={() => setTrail(null)}
        onShowOnBoard={(stepId) => {
          setTrail(null);
          onShowOnBoard(stepId);
        }}
      />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title={`Take ${removing ? name(removing) : "this lead"} out of the workflow?`}
        description="They get nothing more from this workflow, and they do not enter it again later."
        confirmLabel="Take out"
        destructive
        loading={exit.isPending}
        onConfirm={() => removing && void remove(removing)}
      />
    </div>
  );
}
